import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { fuseAnimations } from '@fuse/animations';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseNavigationItem } from '@fuse/components/navigation/navigation.types';
import { FuseUtilsService } from '@fuse/services/utils/utils.service';
import { ReplaySubject, Subject } from 'rxjs';
import { FuseHorizontalNavigationBasicItemComponent } from './components/basic/basic.component';
import { FuseHorizontalNavigationBranchItemComponent } from './components/branch/branch.component';
import { FuseHorizontalNavigationSpacerItemComponent } from './components/spacer/spacer.component';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'fuse-horizontal-navigation',
    templateUrl: './horizontal.component.html',
    styleUrls: ['./horizontal.component.scss'],
    animations: fuseAnimations,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'fuseHorizontalNavigation',
    standalone: true,
    imports: [
        FuseHorizontalNavigationBasicItemComponent,
        FuseHorizontalNavigationBranchItemComponent,
        FuseHorizontalNavigationSpacerItemComponent,
    ],
})
export class FuseHorizontalNavigationComponent
    implements OnChanges, OnInit, OnDestroy
{
    abs = true;
    private _changeDetectorRef = inject(ChangeDetectorRef);
    private _fuseNavigationService = inject(FuseNavigationService);
    private _fuseUtilsService = inject(FuseUtilsService);
    private _translocoService = inject(TranslocoService);
    menuTitle: string = 'Dashboard';

    @Input() name: string = this._fuseUtilsService.randomId();
    @Input() navigation: FuseNavigationItem[];

    onRefreshed: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    showDashboardItems: boolean = false;

    private routeTranslationMap: { [key: string]: string } = {
        // Main routes
        dashboard: 'navigation.dashboard',
        players: 'navigation.players.title',
        financials: 'navigation.financials.title',
        'casino-content': 'navigation.casinoContent',
        'sport-content': 'navigation.sportContent',
        'promotional-tools': 'navigation.promotions',
        bonuses: 'promotional.breadcrumbs.bonuses',
        reports: 'navigation.reports.title',
        affiliates: 'navigation.affiliates',
        users: 'navigation.users.title',
        email: 'navigation.email.title',
        settings: 'navigation.settings.title',
        home: 'navigation.dashboard',

        // Financials sub-routes
        cashier: 'navigation.financials.cashier',
        transactions: 'navigation.financials.transactions',
        'exchange-log': 'Exchange Log',
        'verification-queue': 'navigation.players.verification-queue',
        withdrawals: 'navigation.players.withdrawals',

        // Reports sub-routes
        'sport-book': 'navigation.reports.sport-book.title',
        bets: 'navigation.reports.sport-book.bets',
        sports: 'reports.sport-book.sports.title',
        competitions: 'navigation.reports.sport-book.competitions',
        'market-types': 'navigation.reports.sport-book.market-types',
        casino: 'navigation.reports.casino.title',
        game: 'navigation.reports.casino.game',
        player: 'navigation.reports.casino.player',
        provider: 'navigation.reports.casino.provider',
        'player-game': 'navigation.reports.casino.player-game',
        financial: 'navigation.reports.financial.title',
        'payments-report': 'navigation.reports.financial.payments',

        // Users sub-routes
        admins: 'navigation.users.admin',
        'admin-groups': 'navigation.users.admin-group',
        'automatic-groups': 'navigation.users.automatic-admin-group',
        managers: 'navigation.users.managers',
        'manager-groups': 'navigation.users.manager-groups',
        operators: 'navigation.users.operators',
        'operator-groups': 'navigation.users.operator-groups',

        // Email sub-routes
        templates: 'navigation.email.templates',
        logs: 'navigation.email.logs',

        // Casino Management sub-routes (new module, not reports)
        'casino-games': 'navigation.casino.games',
        'casino-categories': 'navigation.casino.categories',
        'casino-providers': 'navigation.casino.providers',
        'casino-logs': 'navigation.casino.auditLogs',

        // Settings sub-routes
        stories: 'navigation.settings.stories',
        offers: 'navigation.settings.offers',
        notifications: 'navigation.settings.notifications',
        highlights: 'navigation.settings.highlights',
        'payment-providers': 'Payment Providers',

        // Template editor sub-routes
        template: 'navigation.template-editor',
        editor: 'settings.template-editor.sports.title',
        markets: 'settings.template-editor.markets.title',

        // Players sub-routes
        'messages-list': 'players.messages-list.title',
        'player-register-sessions': 'players.register-sessions.title',

        // Promotional tools sub-routes
        'bonus-config': 'promotional.breadcrumbs.bonuses',
    };

    constructor(
        private activatedRoute: ActivatedRoute,
        private router: Router
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On changes
     *
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        // Navigation
        if ('navigation' in changes) {
            // Mark for check
            this._changeDetectorRef.markForCheck();
        }
    }

    /**
     * On init
     */
    ngOnInit(): void {
        // Make sure the name input is not an empty string
        if (this.name === '') {
            this.name = this._fuseUtilsService.randomId();
        }

        // Wait for translations to be loaded first
        const translationsLoaded$ = this._translocoService.selectTranslate(
            'navigation.dashboard'
        );
        translationsLoaded$.subscribe(() => {
            this.updateMenuTitle();
        });

        // Listen for language changes
        this._translocoService.langChanges$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                // Wait for translations to be loaded before updating
                const translationsLoaded$ =
                    this._translocoService.selectTranslate(
                        'navigation.dashboard'
                    );
                translationsLoaded$.subscribe(() => {
                    this.updateMenuTitle();
                    this._changeDetectorRef.markForCheck();
                });
            });

        this.router.events.subscribe(() => {
            this.updateMenuTitle();
            // Auto-close the dashboard menu on route change
            if (this.showDashboardItems) {
                this.showDashboardItems = false;
                this._changeDetectorRef.markForCheck();
            }
        });

        this._fuseNavigationService.registerComponent(this.name, this);
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Deregister the navigation component from the registry
        this._fuseNavigationService.deregisterComponent(this.name);

        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Refresh the component to apply the changes
     */
    refresh(): void {
        // Mark for check
        this._changeDetectorRef.markForCheck();

        // Execute the observable
        this.onRefreshed.next(true);
    }

    /**
     * Track by function for @for loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    showDashboard() {
        this.showDashboardItems = !this.showDashboardItems;
    }

    private updateMenuTitle(): void {
        const pathArray = this.router.url.split('/');
        const routeNames = pathArray.filter(Boolean);
        if (routeNames.length > 0) {
            const formattedTitle = routeNames
                .map((name) => this.getTranslatedRouteName(name))
                .join('    /    ');
            this.menuTitle = formattedTitle;
        } else {
            this.menuTitle = this._translocoService.translate(
                'navigation.dashboard'
            );
        }
    }

    private getTranslatedRouteName(routeName: string): string {
        const fullPath = this.router.url;
        const isCasinoManagement = fullPath.startsWith('/casino/') && !fullPath.startsWith('/reports/casino/');

        // Special handling for 'casino' route - check if it's the new casino management module
        // by checking the full URL path
        if (routeName === 'casino') {
            // If it's /casino/* (new module), use casino management translation
            // If it's /reports/casino/* (reports), use reports casino translation
            if (isCasinoManagement) {
                return this._translocoService.translate('navigation.casino.title');
            }
            // Otherwise it's the reports casino
            return this._translocoService.translate('navigation.reports.casino.title');
        }

        // Special handling for casino management sub-routes
        if (isCasinoManagement) {
            const casinoSubRouteMap: { [key: string]: string } = {
                'games': 'navigation.casino.games',
                'categories': 'navigation.casino.categories',
                'providers': 'navigation.casino.providers',
                'logs': 'navigation.casino.auditLogs',
            };
            if (casinoSubRouteMap[routeName]) {
                return this._translocoService.translate(casinoSubRouteMap[routeName]);
            }
        }

        // Check if we have a translation key for this route
        const translationKey = this.routeTranslationMap[routeName];
        if (translationKey) {
            return this._translocoService.translate(translationKey);
        }

        // Fallback to capitalized route name if no translation found
        return this.capitalizeTitle(routeName);
    }

    private capitalizeTitle(title: string): string {
        return title
            .replace(/-/g, ' ')
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    /**
     * Handle mouseenter event for submenus
     *
     * @param item Navigation item
     */
    onSubmenuMouseEnter(item: FuseNavigationItem): void {
        // You can add specific behavior here if needed
        // For now, we'll just mark for change detection
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Handle mouseleave event for submenus
     *
     * @param item Navigation item
     */
    onSubmenuMouseLeave(item: FuseNavigationItem): void {
        // You can add specific behavior here if needed
        // For now, we'll just mark for change detection
        this._changeDetectorRef.markForCheck();
    }
}
